import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import PortalBottomNav from "../components/PortalBottomNav";
import PortalHeader from "../components/PortalHeader";
import {
  ArrowLeft,
  MapPin,
  Car,
  Train,
  Phone,
  Clock,
  Sun,
  Cloud,
  Thermometer,
  ChevronDown,
  ChevronUp,
  Check,
  Home,
  Coffee,
  Users,
  Wifi,
  Map,
  Plane,
  Mail,
  Copy,
  Navigation,
  Bell,
  User
} from "lucide-react";

// Arrival instructions data
const ARRIVAL_SECTIONS = [
  {
    id: "driving",
    title: "By Car from Valencia",
    icon: Car,
    content: `
**From Valencia Airport (VLC):**
1. Exit the airport and take the CV-500 toward Valencia
2. Merge onto the A-7 / E-15 heading north toward Barcelona
3. Take exit 473 for CV-310 toward Náquera/Bétera
4. Follow CV-310 for about 15km until you reach Náquera
5. Villa Savi is located at the edge of the village near Sierra Calderona

**Drive time:** ~35 minutes
**Parking:** Free parking available at the villa
    `.trim(),
  },
  {
    id: "taxi",
    title: "By Taxi",
    icon: Phone,
    content: `
**From Valencia Airport:**
- Expect to pay €45-55 for a taxi to Náquera
- Journey takes approximately 35-40 minutes
- We recommend booking in advance

**Recommended Taxi Companies:**
- Radio Taxi Valencia: +34 963 703 333
- Taxi Aeropuerto: +34 963 525 252

**Tip:** Ask the driver for "Náquera, Sierra Calderona" and we'll send you the exact pin location.
    `.trim(),
  },
  {
    id: "public",
    title: "By Public Transport",
    icon: Train,
    content: `
**Option 1: Metro + Bus**
1. Take the Metro Line 3/5 from Airport to Xàtiva station
2. Transfer to Line 1 toward Bétera
3. Get off at Bétera station
4. Take the local bus to Náquera (runs every hour)

**Option 2: Bus from Valencia City**
- Autobuses Metropolitanos runs buses to Náquera from Valencia
- Check schedules at www.atmv.es

**Total journey time:** 1.5-2 hours
**Note:** The last bus to Náquera is around 8pm
    `.trim(),
  },
];

const FIRST_WEEK_TIPS = [
  {
    icon: Home,
    title: "Settle In",
    desc: "Take your time to explore the villa. Find your favorite spots to work and relax."
  },
  {
    icon: Coffee,
    title: "Morning Kitchen Vibes",
    desc: "People start milling around the kitchen from 9am. Grab a coffee and meet your housemates."
  },
  {
    icon: Users,
    title: "Introduce Yourself",
    desc: "Don't be shy! Everyone was new once. People here love meeting new humans."
  },
  {
    icon: Map,
    title: "Explore Náquera",
    desc: "Take a walk around the village. Visit the local bakery and enjoy the mountain views."
  },
  {
    icon: Wifi,
    title: "Find Your Rhythm",
    desc: "Work when you work best. The quiet hours (10pm-8am) help everyone focus and rest."
  },
];

const ArrivalPage = () => {
  const { user, token } = useAuth();
  const [weather, setWeather] = useState(null);
  const [expandedSection, setExpandedSection] = useState("driving");

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    try {
      const res = await axios.get(`${API}/weather`);
      setWeather(res.data);
    } catch (error) {
      console.error("Error fetching weather:", error);
    }
  };

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  const arrivalDate = user?.arrival?.arrival_date || user?.room?.start_date;
  const formattedDate = arrivalDate 
    ? new Date(arrivalDate).toLocaleDateString('en-GB', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      })
    : "Soon";

  return (
    <div className="min-h-screen bg-[#FDFCF8] pb-24">
      {/* Header */}
      <PortalHeader title="Your Arrival" back="/dashboard?tab=profile" testid="arrival-header" />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          
          {/* Arrival Banner */}
          <div className="bg-gradient-to-br from-[#C85A38] to-[#F2CC8F] rounded-3xl p-6 text-white">
            <p className="text-white/80 text-sm">You're arriving on</p>
            <h2 className="font-serif text-2xl mt-1">{formattedDate}</h2>
            <div className="mt-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">Náquera, Valencia</span>
              </div>
              {user?.arrival?.arrival_time && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">{user.arrival.arrival_time}</span>
                </div>
              )}
            </div>
          </div>

          {/* Weather Widget */}
          {weather && (
            <div className="bg-white rounded-3xl p-5 border border-stone-100">
              <h3 className="font-medium text-[#1A1A1A] mb-3">Current Weather in Náquera</h3>
              <div className="flex items-center gap-4">
                <div className="text-4xl">
                  {weather.description?.toLowerCase().includes('sun') ? '☀️' : 
                   weather.description?.toLowerCase().includes('cloud') ? '⛅' : 
                   weather.description?.toLowerCase().includes('rain') ? '🌧️' : '🌤️'}
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#1A1A1A]">{weather.temp}°C</p>
                  <p className="text-sm text-[#1A1A1A]/60">{weather.description}</p>
                </div>
                <div className="ml-auto text-right text-sm text-[#1A1A1A]/60">
                  <p>Feels like {weather.feels_like}°C</p>
                  <p>Humidity {weather.humidity}%</p>
                </div>
              </div>
            </div>
          )}

          {/* Getting Here */}
          <div className="space-y-3">
            <h3 className="font-serif text-lg text-[#1A1A1A]">Getting to Savi</h3>
            
            {ARRIVAL_SECTIONS.map((section) => (
              <div
                key={section.id}
                className="bg-white rounded-2xl border border-stone-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-5 py-4 flex items-center gap-3 text-left"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#F0EDE3] flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-[#C85A38]" />
                  </div>
                  <span className="flex-1 font-medium text-[#1A1A1A]">{section.title}</span>
                  {expandedSection === section.id ? (
                    <ChevronUp className="w-5 h-5 text-[#1A1A1A]/40" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#1A1A1A]/40" />
                  )}
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    expandedSection === section.id ? "max-h-[1000px]" : "max-h-0"
                  }`}
                >
                  <div className="px-5 pb-5 text-sm text-[#1A1A1A]/80 whitespace-pre-line">
                    {section.content.split('**').map((part, i) => 
                      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Steve's Contact for Pickup */}
          <div className="bg-[#8C9A7C]/20 rounded-2xl p-5">
            <h3 className="font-medium text-[#1A1A1A] mb-2">Need Airport Pickup? 🚗</h3>
            <p className="text-sm text-[#1A1A1A]/70 mb-3">
              Steve can arrange a pickup from Valencia airport. Message him at least 48 hours before arrival.
            </p>
            <a 
              href="https://wa.me/34632628051" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm"
            >
              <Phone className="w-4 h-4" />
              Message Steve on WhatsApp
            </a>
          </div>

          {/* First Week Tips */}
          <div className="bg-gradient-to-br from-[#F0EDE3] to-white rounded-3xl p-6">
            <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Your First Week at Savi 🌟</h3>
            <p className="text-sm text-[#1A1A1A]/70 mb-4">
              Here's what to expect and how to make the most of your first days:
            </p>
            
            <div className="space-y-4">
              {FIRST_WEEK_TIPS.map((tip, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                    <tip.icon className="w-5 h-5 text-[#C85A38]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{tip.title}</p>
                    <p className="text-sm text-[#1A1A1A]/60">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <div className="bg-white rounded-3xl p-5 border border-stone-100">
            <h3 className="font-medium text-[#1A1A1A] mb-4">Before You Arrive ✓</h3>
            <div className="space-y-3">
              {[
                "Complete your onboarding profile",
                "Join the Savi WhatsApp group",
                "Read the Guest Handbook",
                "Confirm your arrival time with Steve",
                "Pack for mountain weather (layers!)",
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#8C9A7C]/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#8C9A7C]" />
                  </div>
                  <span className="text-sm text-[#1A1A1A]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Map Embed */}
          <div className="bg-white rounded-3xl p-5 border border-stone-100">
            <h3 className="font-medium text-[#1A1A1A] mb-3">📍 Villa Savi Location</h3>
            <div className="aspect-video bg-[#F0EDE3] rounded-2xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1538.5!2d-0.395298!3d39.6566473!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd60696da5494fcd%3A0x72138cba01e77bda!2sSavi%20Coliving%20Valencia!5e0!3m2!1sen!2ses!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Villa Savi Location"
              />
            </div>
            <a 
              href="https://www.google.com/maps/place/Savi+Coliving+Valencia/@39.6566473,-0.395298,856m"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-[#C85A38] text-sm"
            >
              <Map className="w-4 h-4" />
              Open in Google Maps
            </a>
          </div>

        </div>
      </div>

      {/* Bottom Navigation */}
      <PortalBottomNav />
    </div>
  );
};

export default ArrivalPage;
